import { memo, useEffect, useMemo, useRef, useState } from 'react'

import * as DropdownMenu from '@radix-ui/react-dropdown-menu'
import * as Toolbar from '@radix-ui/react-toolbar'
import { AnimatePresence, motion } from 'framer-motion'
import {
  Briefcase,
  Check,
  ChevronDown,
  ClipboardList,
  Copy,
  Lightbulb,
  Lock,
  MessageCircle,
  PartyPopper,
  TextCursorInput,
} from 'lucide-react'

import { useHover } from '@/hooks/layout/useHover'
import { useThemeSync } from '@/hooks/layout/useThemeSync'
import { cn } from '@/lib/utils'

import { platformDisplays } from '../layout/PlatformIcons'
import { Streamdown } from 'streamdown'

const toneDisplay = {
  standard: {
    Icon: TextCursorInput,
    displayText: 'Standard',
  },
  formal: {
    Icon: Briefcase,
    displayText: 'Formal',
  },
  conversational: {
    Icon: MessageCircle,
    displayText: 'Conversational',
  },
  enthusiastic: {
    Icon: PartyPopper,
    displayText: 'Enthusiastic',
  },
  inspirational: {
    Icon: Lightbulb,
    displayText: 'Inspiring',
  },
  informative: {
    Icon: ClipboardList,
    displayText: 'Informative',
  },
}

const messageContainerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { duration: 0.5, ease: 'easeOut' },
  },
  exit: {
    opacity: 0,
    transition: { duration: 0.5, ease: 'easeIn' },
  },
}

// When swapping between streaming and static content, we don't want the
// previous content to fade to `opacity: 0` (which reads as "the message
// disappeared")—especially because we use AnimatePresence.
const contentSwapVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { duration: 0.15, ease: 'easeOut' },
  },
  // No fade-out on swap; new content will take over immediately.
  exit: {
    opacity: 1,
    transition: { duration: 0 },
  },
}

const formatToneLabel = label =>
  toneDisplay[label]?.displayText ??
  label?.replace(/[-_]/g, ' ')?.replace(/^\w|\s\w/g, c => c.toUpperCase())

const ToneDropdown = ({
  canUpdateMessage,
  variations,
  selectedTone,
  handleToneSelect,
  standardContent,
  className,
}) => {
  const [open, setOpen] = useState(false)
  const [hoveredTone, setHoveredTone] = useState(null)
  const toneOptions = useMemo(
    () => [
      {
        label: 'standard',
        Icon: toneDisplay['standard']?.Icon,
        displayText: formatToneLabel('standard'),
        content: standardContent,
      },
      ...variations.map(variation => ({
        label: variation.label,
        Icon: toneDisplay[variation.label]?.Icon,
        displayText: formatToneLabel(variation.label),
        content: variation.content,
      })),
    ],
    [standardContent, variations],
  )
  useEffect(() => {
    if (!open) setHoveredTone(null)
  }, [open])

  const baseMenuItem =
    'group relative py-3 flex h-7 select-none items-center rounded-[3px] pl-[5px] text-sm leading-none text-plum-dim outline-none data-[disabled]:pointer-events-none data-[highlighted]:bg-plum-dim data-[disabled]:text-mauve-8 data-[highlighted]:text-plum-dim'
  const dropDownMenuItem = cn(baseMenuItem)
  const activeDropDownMenuItem = cn(baseMenuItem, 'bg-plum-9 text-plum-1')
  const previewDropDownMenuItem = cn(baseMenuItem, 'bg-plum-ui')
  const selectedOption = toneOptions.find(
    option => option.label === selectedTone,
  )
  const SelectedToneIcon = selectedOption?.Icon
  const SelectedToneDisplayText = selectedOption?.displayText

  const previewTarget = hoveredTone ?? selectedOption

  return toneOptions.length > 1 ? (
    <DropdownMenu.Root
      open={open}
      onOpenChange={value => {
        setOpen(value)
        if (!value) setHoveredTone(null)
      }}
    >
      <DropdownMenu.Trigger asChild>
        <div
          className={cn(
            'bg-blacka-ui flex items-center gap-1 rounded-full outline-none transition-colors text-xs',
            className,
            !canUpdateMessage && 'bg-blacka-ghost',
          )}
        >
          {SelectedToneIcon &&
            (canUpdateMessage ? (
              <SelectedToneIcon className="text-foreground" size={15} />
            ) : (
              <Lock size={12} className="text-foreground" />
            ))}
          <span className="font-medium tracking-wide text-foreground text-2xs">
            {SelectedToneDisplayText}
          </span>
          <ChevronDown className="size-4 text-foreground opacity-70" />
        </div>
      </DropdownMenu.Trigger>
      <DropdownMenu.Portal align="start">
        <AnimatePresence>
          {open && (
            <DropdownMenu.Content
              className={cn(
                'z-50 data-[side=bottom]:animate-slideUpAndFade data-[side=left]:animate-slideRightAndFade data-[side=right]:animate-slideLeftAndFade data-[side=top]:animate-slideDownAndFade',
                'border-gray-normal flex min-w-[220px] overflow-hidden rounded-md border',
                'bg-whitea-ui border bg-clip-padding p-2 shadow-[0px_10px_38px_-10px_rgba(22,_23,_24,_0.35),_0px_10px_20px_-15px_rgba(22,_23,_24,_0.2)] backdrop-blur-xl will-change-[opacity,transform] focus:ring-0',
              )}
              sideOffset={5}
              forceMount
            >
              <div className="flex h-[clamp(240px,45vh,520px)] w-[220px] flex-col gap-1 overflow-y-auto p-1 pr-2">
                {toneOptions.map(option => {
                  const isActive = option.label === selectedTone
                  const isPreviewed = option.label === previewTarget?.label
                  const ToneIcon = option.Icon
                  return (
                    <DropdownMenu.CheckboxItem
                      key={option.label}
                      checked={isActive}
                      onCheckedChange={() =>
                        canUpdateMessage
                          ? handleToneSelect(option.label)
                          : undefined
                      }
                      onPointerEnter={() => setHoveredTone(option)}
                      className={cn(
                        isPreviewed
                          ? previewDropDownMenuItem
                          : isActive
                            ? activeDropDownMenuItem
                            : dropDownMenuItem,
                        !canUpdateMessage && 'saturate-0',
                      )}
                    >
                      <div className="pr-5">
                        {ToneIcon && <ToneIcon size={15} />}
                      </div>
                      {option.displayText}
                      <DropdownMenu.ItemIndicator className="absolute right-0 inline-flex w-[25px] items-center justify-center">
                        {canUpdateMessage ? (
                          <Check size={15} />
                        ) : (
                          <Lock size={15} />
                        )}
                      </DropdownMenu.ItemIndicator>
                    </DropdownMenu.CheckboxItem>
                  )
                })}
              </div>
              <div className="dark:text-muted-foreground/90 relative hidden h-[clamp(240px,45vh,520px)] w-[clamp(260px,32vw,520px)] border-l border-black/5 leading-snug text-[clamp(10px,1.2vw,12px)] sm:block dark:border-white/10">
                {previewTarget ? (
                  <div className="relative h-full overflow-y-auto pb-3 pl-3 pr-3 pt-3">
                    {previewTarget.Icon && (
                      <div className="pointer-events-none fixed right-3 top-3 z-[2] flex justify-end">
                        <div className="flex items-center justify-center gap-1 rounded-full bg-black/5 p-1 shadow-sm backdrop-blur-sm dark:bg-white/10">
                          <previewTarget.Icon
                            size={15}
                            className={cn('text-plum-11', 'size-4')}
                          />
                        </div>
                      </div>
                    )}
                    {!canUpdateMessage && (
                      <div className="bg-blacka-app text-blacka-dim absolute bottom-0 right-0 z-[2] flex scale-90 items-center gap-1 rounded-sm px-2 py-1 font-semibold uppercase tracking-wider text-2xs">
                        <Lock
                          size={15}
                          className={cn('text-blacka-dim', 'size-3')}
                        />
                        <span>View Only</span>
                      </div>
                    )}
                    <div className="prose prose-sm max-w-none pt-2 text-foreground dark:prose-invert">
                      {previewTarget.content ? (
                        <Streamdown
                          controls={{ table: true }}
                        >
                          {previewTarget.content}
                        </Streamdown>
                      ) : (
                        <span>Draft is still arriving…</span>
                      )}
                    </div>
                  </div>
                ) : (
                  <span className="opacity-70">
                    Hover a tone to peek at the opening lines.
                  </span>
                )}
              </div>
            </DropdownMenu.Content>
          )}
        </AnimatePresence>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  ) : null
}
/**
 * AgentMessage – a single message from the agent.
 *
 * Key change: we always render with Streamdown and pass `isAnimating` when
 * streaming. No manual DOM writing, no completedBlocks, no separate
 * StreamingContent component.
 */
export function AnimatedLoadingDots({ count = 3, interval = 0.4 }) {
  const [visibleCount, setVisibleCount] = useState(0)
  useEffect(() => {
    const timer = setInterval(() => {
      setVisibleCount(v => (v + 1) % (count + 1))
    }, interval * 1000)
    return () => clearInterval(timer)
  }, [count, interval])
  return (
    <span aria-live="polite" aria-label="Loading" className="ml-1 inline-flex">
      {Array.from({ length: count }).map((_, i) => (
        <motion.span
          key={i}
          initial={{ opacity: 0.2, scale: 0.8 }}
          animate={visibleCount > i ? { opacity: 1, scale: 1 } : { opacity: 0.2, scale: 0.8 }}
          transition={{ duration: 0.25 }}
          className="mx-0.5"
          style={{ fontSize: '1.5em', lineHeight: 1 }}
        >
          .
        </motion.span>
      ))}
    </span>
  )
}

export const AgentMessage = memo(
  function AgentMessage({
    clientId,
    isStreaming,
    content,
    meta,
    setLastAgentMessageRef,
  }) {
    const { handleMouseEnter, handleMouseLeave } = useHover()

    const renderedContent = content ?? ''

    const platformDisplay = meta?.platform
      ? platformDisplays[meta?.platform]
      : null
    const PlatformIcon = platformDisplay?.Icon

    const { isDark } = useThemeSync()
    const color = platformDisplay?.color || (isDark ? 'white' : 'currentColor')

    const fallbackIdRef = useRef(
      `agent-${Math.random().toString(36).slice(2, 10)}`,
    )
    const layoutKey = useMemo(() => {
      const existingKey =
        meta?.layoutKey ??
        meta?.id ??
        meta?.message_id ??
        meta?.messageId ??
        clientId
      if (existingKey) return existingKey
      return fallbackIdRef.current
    }, [clientId, meta?.id, meta?.layoutKey, meta?.messageId, meta?.message_id])
    const contentLayoutId = `agent-message-${layoutKey}`

    return (
      <motion.div
        ref={setLastAgentMessageRef}
        layout
        variants={messageContainerVariants}
        initial="hidden"
        animate="visible"
        exit="exit"
      >
        <article className="w-full">
          <div className="m-auto pl-[var(--thread-content-margin)] py-5 text-base [--thread-content-margin:--spacing(4)] @[37rem]:[--thread-content-margin:--spacing(6)] @[72rem]:[--thread-content-margin:--spacing(16)]">
            <div
              tabIndex="-1"
              className="group/turn-messages focus-visible:outline-hidden mx-auto flex max-w-[var(--thread-content-max-width)] flex-1 gap-4 text-base [--thread-content-max-width:32rem] @[34rem]:[--thread-content-max-width:40rem] @[64rem]:[--thread-content-max-width:48rem] md:gap-5 lg:gap-6"
            >
              <div className="relative flex w-full min-w-0 flex-col">
                <div className="relative flex-col gap-1 md:gap-3">
                  <div className="flex max-w-full grow flex-col">
                    <div
                      dir="auto"
                      data-message-author-role="assistant"
                      className="text-message relative flex min-h-8 w-full flex-col items-end gap-2 whitespace-normal break-words text-start [.text-message+&]:mt-5"
                      onMouseEnter={handleMouseEnter}
                      onMouseLeave={handleMouseLeave}
                    >
                      <div className="flex w-full flex-col gap-1 space-y-2 first:pt-[3px] empty:hidden">
                        {PlatformIcon ? (
                          <div
                            className={cn(
                              'absolute mr-2 hidden size-5 translate-x-[-150%] translate-y-1/4 opacity-20 saturate-0 transition-all duration-1000 group-hover/turn-messages:opacity-100 group-hover/turn-messages:saturate-100 lg:block',
                            )}
                          >
                            <PlatformIcon color={color} />
                          </div>
                        ) : null}

                        <AnimatePresence initial={false} mode="sync">
                          <motion.div
                            key={`agent-content-${layoutKey}`}
                            layoutId={contentLayoutId}
                            layout="position"
                            initial="hidden"
                            animate="visible"
                            exit="exit"
                            variants={contentSwapVariants}
                            className="hover:prose-a:text-primary/80 prose prose-sm w-full break-words text-foreground text-sm dark:prose-invert"
                          >
                            <Streamdown isAnimating={isStreaming}>
                              {renderedContent}
                            </Streamdown>
                          </motion.div>
                        </AnimatePresence>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </article>
      </motion.div>
    )
  },
  (prev, next) => {
    // Re-render on content changes or streaming flag changes.
    // Streamdown itself is memoized per-block, so this remains performant.
    return (
      prev.clientId === next.clientId &&
      prev.isStreaming === next.isStreaming &&
      prev.content === next.content
    )
  },
)
